"use client";

import {
    forwardRef,
    type ButtonHTMLAttributes,
    type MouseEvent,
    type ReactNode,
} from "react";
// import { InfoIcon } from "@phosphor-icons/react/ssr";
import InfoIcon from "@/icons/ui/info-fill.svg";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "~/lib/utils";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import { useDrawer, type DrawerOpenOptions } from "~/components/layout/Drawer";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import type { GtmEvent } from "~/lib/gtm/getGtmEventData";

/**
 * Event payload for the badge click, reusing the project-wide GTM typing.
 *
 * `event` defaults to "user_interaction" and `meta_data` is optional so callers
 * only need to pass `event_name`/`widget`; the badge fills in the rest.
 */
export type InfoBadgeEventData = Partial<Pick<GtmEvent, "event">> &
    Omit<GtmEvent["value"], "meta_data"> & {
        meta_data?: GtmEvent["value"]["meta_data"];
    };

const infoBadgeVariants = cva(
    "inline-flex items-center gap-1 rounded-[4px] disabled:opacity-60 disabled:pointer-events-none leading-none",
    {
        variants: {
            variant: {
                default: "bg-gray100 text-gray500",
                secondary: "bg-pureWhite text-error500",
                primary: "bg-pureWhite/25 text-pureWhite",
            },
            size: {                
                md: "px-1.5 pr-2 py-1",
                lg: "px-1.5 pr-2 py-1",
            },
        },
        compoundVariants: [
            {
                variant: "secondary",
                className: "max-md:px-0",
            },
        ],
        defaultVariants: { variant: "default", size: "md" },
    }
);

const iconColorByVariant: Record<
    NonNullable<VariantProps<typeof infoBadgeVariants>["variant"]>,
    string
> = {
    default: "text-gray400",
    secondary: "text-gray300",
    primary: "text-gray100",
};

type InfoBadgeOwnProps = {
    label: ReactNode;
    icon?: ReactNode;
    showIcon?: boolean;
    drawer?: DrawerOpenOptions;
    iconClassName?: string;
    labelClassName?: string;
    /** Tracking payload sent on click. */
    eventData?: InfoBadgeEventData;
};

export type InfoBadgeProps = InfoBadgeOwnProps &
    VariantProps<typeof infoBadgeVariants> &
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">;

const InfoBadge = forwardRef<HTMLButtonElement, InfoBadgeProps>(
    (
        {
            label,
            icon,
            showIcon = true,
            drawer,
            variant,
            size,
            className,
            iconClassName,
            labelClassName,
            eventData,
            onClick,
            type = "button",
            "aria-label": ariaLabel,
            ...rest
        },
        ref
    ) => {
        const { open } = useDrawer();
        const sendEvent = useTrackEvent();
        const resolvedVariant = variant ?? "default";

        const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            if (drawer) open(drawer);

            if (eventData) {
                const {
                    event: eventName = "user_interaction",
                    meta_data,
                    ...eventValue
                } = eventData;

                sendEvent({
                    event: eventName,
                    value: {
                        ...eventValue,
                        meta_data: {
                            click_text: "Total Value",
                            ...meta_data,
                        },
                    },
                });
            }

            onClick?.(event);
        };

        const computedAriaLabel =
            ariaLabel ??
            (typeof label === "string" ? `${label}, view details` : undefined);

        return (
            <button
                ref={ref}
                type={type}
                onClick={handleClick}
                aria-label={computedAriaLabel}
                className={cn(infoBadgeVariants({ variant, size }), className)}
                {...rest}
            >
                <Paragraph size="xs" sizes={{ md: size === "lg" ? "sm" : "xs" }} className={cn(labelClassName, "!leading-none pt-0.5")}>
                    {label}
                </Paragraph>
                {showIcon &&
                    (icon ?? (
                        <InfoIcon
                            width={13}
                            height={13}
                            className={cn(
                                iconColorByVariant[resolvedVariant],
                                iconClassName
                            )}
                        />
                    ))}
            </button>
        );
    }
);

InfoBadge.displayName = "InfoBadge";

export default InfoBadge;
